import { useEffect, useMemo, useState, JSX, memo } from 'react'

import { withSearch } from '@elastic/react-search-ui'
import type { SearchContextState } from '@elastic/react-search-ui/lib/cjs/withSearch'
import { helpers } from '@elastic/search-ui'
import { useIntl } from 'react-intl'
import { RefetchQueryFilters, useQueryClient } from 'react-query'

import { CheckboxProps } from 'app/components/base/Checkbox'
import Text from 'app/components/base/Text'
import MissingVehicleDialog from 'app/components/dialogs/MissingVehicleDialog'
import { transformGenerationOptions } from 'app/components/forms/template/GeneralListingFormTemplate/helpers/carInfo.helpers'
import useCurrencyState from 'app/hooks/useCurrencyState'
import useEffectAfterMount from 'app/hooks/useEffectAfterMount'
import useModal from 'app/hooks/useModal'
import CheckboxFilter from 'app/libs/elastic/filters/CheckboxFilter'
import SelectFilter, {
  SelectFilterProps
} from 'app/libs/elastic/filters/SelectFilter'
import SliderRangeFilter, {
  SliderRangeFilterProps
} from 'app/libs/elastic/filters/SliderRangeFilter'
import GoogleReCaptchaV3Provider from 'app/providers/GoogleRecaptchaV3Provider'
import {
  getCategories,
  getMakes,
  getModels,
  getSubCategories,
  getVehicleSeries,
  getVehicleTrims
} from 'app/services/Api/listing'
import { useGetListingsPriceRang } from 'app/services/Query/listing/listing.query'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { MAKE_SOURCE_TYPE } from 'app/utils/constants/listing.constants'
import { getSubcategoryHeader } from 'app/utils/helpers/listings.helpers'
import {
  transformIntlOptions,
  transformPartNumber
} from 'app/utils/helpers/transforms.helper'

import { generateYearsOptions } from '../../CreateListingFeature/helpers/general.helpers'
import {
  conditionOptions,
  itemTypeOptions,
  shippingTypeOptions,
  warrantyOptions,
  saleTypeOptions
} from '../utils/search-feature.options'

type SearchSelectFilterProps = Omit<SelectFilterProps, 'field' | 'filterType'>
type SearchRangeFilterProps = Omit<
  SliderRangeFilterProps,
  'field' | 'filterType'
>

const { findFilterValues } = helpers

type DependentFilterProps = {
  field: string
  filterType: 'any' | 'all'
  dependentField: string
  helperFieldName?: string
  children: (props: any) => JSX.Element
}

type DependentFilterContext = Pick<
  SearchContextState,
  'filters' | 'removeFilter'
>

const DependentFilter = withSearch<
  DependentFilterProps,
  DependentFilterContext
>(({ filters, removeFilter }) => ({
  filters,
  removeFilter
}))(
  ({
    field,
    filterType,
    dependentField,
    removeFilter,
    filters,
    helperFieldName,
    children
  }) => {
    const values = findFilterValues(filters || [], field, filterType)
    const helperValue = findFilterValues(
      filters || [],
      helperFieldName,
      filterType
    )
    const stringified = values?.join()

    useEffectAfterMount(() => {
      if (!stringified) {
        removeFilter(dependentField, undefined, filterType)
      }
    }, [stringified])

    if (typeof children === 'function') {
      return children({ values, helperValue })
    }

    return children
  }
)

export const PartNumberFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <SelectFilter
      field="part_number.keyword"
      divider
      label={formatMessage({ defaultMessage: 'Part Number' })}
      withFilter
      withCreateOption
      transformValue={transformPartNumber}
      withOptions
      removeEmptyOptions
      {...props}
    />
  )
})

export const MakeFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()
  const { open, showModal, hideModal } = useModal()
  const [isEnabled, setEnabled] = useState(false)

  const handleOpen = () => {
    if (isEnabled) return

    setEnabled(true)
  }

  return (
    <>
      <SelectFilter
        field="makes.keyword"
        dependentField="models"
        divider
        filterType="any"
        sortOption
        label={formatMessage({ defaultMessage: 'Make' })}
        helperText={
          <Text
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              ...(!IS_NATIVE_APP && {
                '&:hover': { cursor: 'pointer' }
              })
            }}
            onClick={showModal}
          >
            {formatMessage({ defaultMessage: 'Vehicle Missing?' })}
          </Text>
        }
        optionSettings={{
          enabled: isEnabled,
          key: 'makes',
          loadOptions: () => getMakes([MAKE_SOURCE_TYPE.imported]),
          optionValue: 'name',
          optionLabel: 'name'
        }}
        withFilter
        onOpen={handleOpen}
        {...props}
      />

      {open && (
        <GoogleReCaptchaV3Provider>
          <MissingVehicleDialog
            open={open}
            onClose={hideModal}
            onConfirm={hideModal}
          />
        </GoogleReCaptchaV3Provider>
      )}
    </>
  )
})

export const ModelFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <DependentFilter
      field="makes.keyword"
      filterType="any"
      dependentField="models.keyword"
    >
      {({ values }) => (
        <SelectFilter
          field="models.keyword"
          dependentField="generation_id.keyword"
          divider
          filterType="any"
          sortOption
          label={formatMessage({ defaultMessage: 'Model' })}
          optionSettings={{
            key: values,
            enabled: !!values?.length,
            loadOptions: () => getModels({ by_make_name: values }),
            optionValue: 'name',
            optionLabel: 'name'
          }}
          disabled={!values?.length}
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
})

export const GenerationFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()

  return (
    <DependentFilter
      field="models.keyword"
      filterType="any"
      dependentField="generation_id.keyword"
      helperFieldName="makes.keyword"
    >
      {({ values, helperValue }) => (
        <SelectFilter
          field="generation_id.keyword"
          dependentField="serie_id.keyword"
          divider
          filterType="any"
          sortOption
          label={formatMessage({ defaultMessage: 'Generation' })}
          optionSettings={{
            key: values,
            enabled: !!values?.length && !!helperValue?.length,
            loadOptions: async () => {
              await queryClient.refetchQueries({
                queryKey: ['options', helperValue],
                type: 'active',
                exact: true
              } as RefetchQueryFilters)

              const cacheDataFromModels = queryClient.getQueryData<Model[]>(
                ['options', helperValue],
                {
                  exact: false
                }
              )

              const modelIndex = cacheDataFromModels?.findIndex(
                (model) => model.name === values[0]
              )

              if (modelIndex === -1 || modelIndex === undefined) return []

              const modelId = cacheDataFromModels?.[modelIndex]?.id

              return transformGenerationOptions([modelId!])
            },
            optionValue: 'id',
            optionLabel: 'name'
          }}
          disabled={!values?.length}
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
})

export const SeriesFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <DependentFilter
      field="generation_id.keyword"
      filterType="any"
      dependentField="serie_id.keyword"
    >
      {({ values }) => (
        <SelectFilter
          field="serie_id.keyword"
          dependentField="trim_id.keyword"
          divider
          filterType="any"
          sortOption
          fullWidth
          label={formatMessage({ defaultMessage: 'Series' })}
          optionSettings={{
            key: values,
            enabled: !!values?.length,
            loadOptions: () => getVehicleSeries({ byGenerationId: values }),
            optionValue: 'id',
            optionLabel: 'name'
          }}
          disabled={!values?.length}
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
})

export const TrimFilter = memo((props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <DependentFilter
      field="serie_id.keyword"
      filterType="any"
      dependentField="trim_id.keyword"
    >
      {({ values }) => (
        <SelectFilter
          field="trim_id.keyword"
          filterType="any"
          sortOption
          fullWidth
          label={formatMessage({ defaultMessage: 'Trim' })}
          optionSettings={{
            key: values,
            enabled: !!values?.length,
            loadOptions: () => getVehicleTrims({ bySerieId: values }),
            optionValue: 'id',
            optionLabel: 'name'
          }}
          disabled={!values?.length}
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
})

export const YearFilter = memo((props: SearchSelectFilterProps) => {
  const [options] = useState(() => generateYearsOptions(1991))
  const intl = useIntl()

  return (
    <SelectFilter
      field="year"
      filterType="any"
      label={intl.formatMessage({ defaultMessage: 'Year' })}
      optionSettings={{
        options,
        optionLabel: 'label',
        optionValue: 'value'
      }}
      {...props}
    />
  )
})

export const CategoryFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <SelectFilter
      field="category_id.keyword"
      dependentField="sub_category_id.keyword"
      divider
      filterType="any"
      sortOption
      label={formatMessage({ defaultMessage: 'Item Category' })}
      optionSettings={{
        key: 'category',
        loadOptions: getCategories,
        optionValue: 'id',
        optionLabel: 'name'
      }}
      withFilter
      {...props}
    />
  )
}

export const SubCategoryFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <DependentFilter
      field="category_id.keyword"
      filterType="any"
      dependentField="sub_category_id.keyword"
    >
      {({ values }) => (
        <SelectFilter
          field="sub_category_id.keyword"
          filterType="any"
          groupBy={getSubcategoryHeader}
          sortOption
          label={formatMessage({ defaultMessage: 'Sub-Category' })}
          optionSettings={{
            key: values,
            enabled: !!values?.length,
            loadOptions: () => getSubCategories({ by_category_id: values[0] }),
            optionValue: 'id',
            optionLabel: 'name'
          }}
          disabled={!values?.length}
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
}

export const ShippingTypeFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () => transformIntlOptions(shippingTypeOptions, formatMessage),
    [locale]
  )

  return (
    <SelectFilter
      field="shipping_types.keyword"
      filterType="any"
      label={formatMessage({ defaultMessage: 'Shipping' })}
      optionSettings={{
        options,
        optionValue: 'value',
        optionLabel: 'label'
      }}
      {...props}
    />
  )
}

export const PriceFilter = ({
  sliderProps = {},
  ...props
}: SearchRangeFilterProps) => {
  const { formatNumber } = useIntl()
  const { currency } = useCurrencyState()

  const { data, refetch } = useGetListingsPriceRang({})

  const min = Number(data?.min_price) || 0
  const max = Number(data?.max_price) || 999999

  const formatCurrency = (v) =>
    formatNumber(v, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })

  const marks = [
    {
      value: min,
      label: formatCurrency(min)
    },
    {
      value: max,
      label: formatCurrency(max)
    }
  ]

  useEffect(() => {
    if (!currency) return

    refetch()
  }, [currency])

  return (
    <SliderRangeFilter
      field="price"
      direction="row-reverse"
      min={min}
      max={max}
      sx={{ flex: 1 }}
      spacing={3}
      {...props}
      sliderProps={{
        marks,
        getAriaValueText: formatCurrency,
        valueLabelFormat: formatCurrency,
        ...sliderProps,
        sx: {
          '& .MuiSlider-markLabel[data-index="0"]': {
            fontSize: '14px',
            color: 'text.third',
            transform: 'translateX(-8px)'
          },
          '& .MuiSlider-markLabel[data-index="1"]': {
            fontSize: '14px',
            color: 'text.third',
            transform: 'translateX(calc(-100% + 8px))'
          },
          ...sliderProps?.sx
        }
      }}
    />
  )
}

export const CountryFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <SelectFilter
      field="full_country_name.keyword"
      dependentField="city.keyword"
      divider
      filterType="any"
      sortOption
      label={formatMessage({ defaultMessage: 'Country' })}
      withOptions
      withFilter
      {...props}
    />
  )
}

export const CityFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage } = useIntl()

  return (
    <DependentFilter
      field="full_country_name.keyword"
      filterType="any"
      dependentField="city.keyword"
    >
      {({ values }) => (
        <SelectFilter
          field="city.keyword"
          divider
          sortOption
          filterType="any"
          label={formatMessage({ defaultMessage: 'City' })}
          disabled={!values?.length}
          withOptions
          withFilter
          {...props}
        />
      )}
    </DependentFilter>
  )
}

export const ConditionFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () => transformIntlOptions(conditionOptions, formatMessage),
    [locale]
  )

  return (
    <SelectFilter
      field="condition.keyword"
      filterType="any"
      label={formatMessage({ defaultMessage: 'Condition' })}
      optionSettings={{
        options,
        optionValue: 'value',
        optionLabel: 'label'
      }}
      {...props}
    />
  )
}

export const WarrantyFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () => transformIntlOptions(warrantyOptions, formatMessage),
    [locale]
  )

  return (
    <SelectFilter
      field="warranty.keyword"
      filterType="any"
      label={formatMessage({ defaultMessage: 'Warranty' })}
      optionSettings={{
        options,
        optionValue: 'value',
        optionLabel: 'label'
      }}
      {...props}
    />
  )
}

export const ItemTypeFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () => transformIntlOptions(itemTypeOptions, formatMessage),
    [locale]
  )

  return (
    <SelectFilter
      field="item_type.keyword"
      filterType="any"
      label={formatMessage({ defaultMessage: 'Type' })}
      optionSettings={{
        options,
        optionValue: 'value',
        optionLabel: 'label'
      }}
      {...props}
    />
  )
}

export const InternationalShippingFilter = (props: CheckboxProps) => {
  const { formatMessage } = useIntl()

  const handleChange = ({ value, onChange, onRemove }) => {
    const transformValue = value ? 'yes' : 'no'

    if (value) {
      onChange(transformValue)
    } else {
      onRemove(undefined)
    }
  }

  return (
    <CheckboxFilter
      {...props}
      label={formatMessage({ defaultMessage: 'International Shipping' })}
      field="international_shipping.keyword"
      handleChange={handleChange}
    />
  )
}

export const SaleTypeFilter = (props: SearchSelectFilterProps) => {
  const { formatMessage, locale } = useIntl()

  const options = useMemo(
    () => transformIntlOptions(saleTypeOptions, formatMessage),
    [locale]
  )

  return (
    <SelectFilter
      field="sale_type.keyword"
      filterType="any"
      label={formatMessage({ defaultMessage: 'Sale Type' })}
      optionSettings={{
        options,
        optionValue: 'value',
        optionLabel: 'label'
      }}
      {...props}
    />
  )
}
